import * as z from "zod";

// String values sent to the backend for enum-like fields. Keep these aligned
// with the job API.
export const CREATION_STRATEGIES = ["Requirement_Import", "Manual_Job_Post"] as const;
export const COMPENSATION_TYPES = ["annual_salary", "daily_rate", "hourly_rate"] as const;
export const MATCH_VISIBILITY_MODES = ["all_matches", "threshold_only"] as const;
export const BELOW_THRESHOLD_HANDLING = ["keep_filtered", "show_at_end"] as const;
export const PUBLICATION_TYPES = ["Publish_After_Payment", "Schedule_Publication"] as const;
export const PAYMENT_SOURCES = ["wallet", "card"] as const;

export interface JobSkillFormValues {
  // Server id from skill/add; named `_id` because useFieldArray overwrites `id`.
  _id?: string;
  title: string;
  importance: string;
  requiredLevel: string;
  years: number | null;
  // JobSkills enum from @utils/constants: 1 current, 2 this year, 3 last year, 4 2+ years
  lastUsed: number | null;
  howWouldYouLikeToUseThisSkill: string;
  // UI only: true once the current values are saved via skill/add or skill/update.
  isSaved: boolean;
}

// One form holds every step. The backend accepts all of these as optional;
// the step schemas below decide what the UI requires before moving on.
export interface JobFormValues {
  // Step 1 - Requirement source
  creationStrategy: (typeof CREATION_STRATEGIES)[number];
  jobRequirementId: string;
  requirementRefNo: string;
  companyId: string;
  companyName: string;
  showCompanyDetails: boolean;
  // Step 2 - Role details
  classification: string;
  discipline: string;
  title: string;
  positionLevel: number | null;
  location: string;
  workPlaceOption: number | null;
  jobType: number | null;
  jobPostDurationDays: number | null;
  qualificationEligibility: string;
  compensationType: (typeof COMPENSATION_TYPES)[number];
  currency: string;
  salaryMin: string;
  salaryMax: string;
  // Step 3 - Job content
  jobOverview: string;
  keyResponsibilities: string;
  industryType: string;
  salaryAndBenefits: string;
  // Step 4 - Skills and experience
  skills: JobSkillFormValues[];
  // Step 6 - Settings
  matchVisibilityMode: (typeof MATCH_VISIBILITY_MODES)[number];
  reviewThreshold: number;
  belowThresholdHandling: (typeof BELOW_THRESHOLD_HANDLING)[number];
  queueHealthNotificationEnabled: boolean;
  queueHealthMinimum: number;
  // Step 7 - Publication
  publicationType: (typeof PUBLICATION_TYPES)[number];
  publicationDate: string;
  publicationTime: string;
  publicationTimezone: string;
  // Step 8 - Payment
  paymentSource: (typeof PAYMENT_SOURCES)[number];
  // Stripe payment method id of the saved card, when paying by card.
  paymentMethodId: string;
  paymentAuthorised: boolean;
}

export const EMPTY_SKILL: JobSkillFormValues = {
  title: "",
  importance: "",
  requiredLevel: "",
  years: null,
  lastUsed: null,
  howWouldYouLikeToUseThisSkill: "",
  isSaved: false,
};

export const JOB_FORM_DEFAULTS: JobFormValues = {
  creationStrategy: 'Requirement_Import',
  jobRequirementId: "",
  requirementRefNo: "",
  companyId: "",
  companyName: "",
  showCompanyDetails: true,
  classification: "",
  discipline: "",
  title: "",
  positionLevel: null,
  location: "",
  workPlaceOption: null,
  jobType: null,
  jobPostDurationDays: null,
  qualificationEligibility: "",
  compensationType: "annual_salary",
  currency: "GBP",
  salaryMin: "",
  salaryMax: "",
  jobOverview: "",
  keyResponsibilities: "",
  industryType: "",
  salaryAndBenefits: "",
  skills: [{ ...EMPTY_SKILL }],
  matchVisibilityMode: 'threshold_only',
  reviewThreshold: 70,
  belowThresholdHandling: 'keep_filtered',
  queueHealthNotificationEnabled: true,
  queueHealthMinimum: 5,
  publicationType: 'Publish_After_Payment',
  publicationDate: "",
  publicationTime: "",
  publicationTimezone:
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London",
  paymentSource: "wallet",
  paymentMethodId: "",
  paymentAuthorised: false,
};

export const parseAmount = (value: string) =>
  Number(String(value ?? "").replace(/,/g, ""));

export const splitLines = (value: string) =>
  String(value ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

// Stand-in for z.enum: zod 4's z.enum/z.union/z.literal typings use `const`
// type parameters, which need TypeScript 5 (this project is on 4.9).
const oneOf = <T extends string>(values: readonly T[]) =>
  z.custom<T>(
    (value) => values.includes(value as T),
    `Expected one of: ${values.join(", ")}`,
  );

const requiredText = (message: string) => z.string().trim().min(1, message);

const requiredSelect = (message: string) =>
  z
    .number()
    .nullable()
    .refine((value) => value !== null, message);

const requiredAmount = (message: string) =>
  requiredText(message).refine(
    (value) => parseAmount(value) > 0,
    "Enter a valid amount",
  );

export const requirementSourceSchema = z
  .object({
    creationStrategy: oneOf(CREATION_STRATEGIES),
    jobRequirementId: z.string(),
    companyId: requiredText("Select the hiring company"),
  })
  .superRefine((values, ctx) => {
    if (values.creationStrategy === 'Requirement_Import' && !values.jobRequirementId) {
      ctx.addIssue({
        code: "custom",
        path: ["jobRequirementId"],
        message: "Import an approved requirement to continue",
      });
    }
  });

export const roleDetailsSchema = z
  .object({
    classification: requiredText("Select a classification"),
    discipline: requiredText("Select the job function or discipline"),
    title: requiredText("Enter the job title"),
    positionLevel: requiredSelect("Select the experience level"),
    location: requiredText("Enter the location"),
    workPlaceOption: requiredSelect("Select the workplace type"),
    jobType: requiredSelect("Select the employment type"),
    jobPostDurationDays: requiredSelect("Select the job post duration"),
    qualificationEligibility: z.string(),
    compensationType: oneOf(COMPENSATION_TYPES),
    currency: requiredText("Select a currency"),
    salaryMin: requiredAmount("Enter the minimum pay"),
    salaryMax: requiredAmount("Enter the maximum pay"),
  })
  .superRefine((values, ctx) => {
    if (parseAmount(values.salaryMin) > parseAmount(values.salaryMax)) {
      ctx.addIssue({
        code: "custom",
        path: ["salaryMax"],
        message: "Maximum pay must be at least the minimum",
      });
    }
  });

export const jobContentSchema = z.object({
  jobOverview: requiredText("Describe the opportunity"),
  keyResponsibilities: z
    .string()
    .refine(
      (value) => splitLines(value).length > 0,
      "Add at least one responsibility",
    ),
  industryType: requiredText("Enter the industry"),
  salaryAndBenefits: z.string(),
});

export const skillSchema = z.object({
  title: requiredText("Enter the skill"),
  importance: requiredText("Select the importance"),
  requiredLevel: requiredText("Select the required level"),
  years: requiredSelect("Select the minimum experience"),
  lastUsed: requiredSelect("Select the recency"),
  howWouldYouLikeToUseThisSkill: requiredText(
    "Explain how this skill will be used",
  ),
  isSaved: z.boolean(),
});

// Fields the user fills in; "Save Skill" validates only these.
export const SKILL_INPUT_FIELDS = [
  "title",
  "importance",
  "requiredLevel",
  "years",
  "lastUsed",
  "howWouldYouLikeToUseThisSkill",
] as const;

export const skillsSchema = z.object({
  skills: z
    .array(skillSchema)
    .min(1, "Add at least one skill requirement")
    .superRefine((skills, ctx) => {
      skills.forEach((skill, index) => {
        if (!skill.isSaved) {
          ctx.addIssue({
            code: "custom",
            path: [index, "isSaved"],
            message: "Save this skill before continuing",
          });
        }
      });
    }),
});

export const settingsSchema = z
  .object({
    matchVisibilityMode: oneOf(MATCH_VISIBILITY_MODES),
    reviewThreshold: z.number().min(50).max(90),
    belowThresholdHandling: oneOf(BELOW_THRESHOLD_HANDLING),
    queueHealthNotificationEnabled: z.boolean(),
    queueHealthMinimum: z.number(),
  })
  .superRefine((values, ctx) => {
    if (values.queueHealthNotificationEnabled && values.queueHealthMinimum < 1) {
      ctx.addIssue({
        code: "custom",
        path: ["queueHealthMinimum"],
        message: "Choose when to send the alert",
      });
    }
  });

export const publicationSchema = z
  .object({
    publicationType: oneOf(PUBLICATION_TYPES),
    publicationDate: z.string(),
    publicationTime: z.string(),
    publicationTimezone: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.publicationType !== 'Schedule_Publication') return;
    if (!values.publicationDate) {
      ctx.addIssue({
        code: "custom",
        path: ["publicationDate"],
        message: "Pick a publication date",
      });
    }
    if (!values.publicationTime) {
      ctx.addIssue({
        code: "custom",
        path: ["publicationTime"],
        message: "Pick a publication time",
      });
    }
    if (
      values.publicationDate &&
      values.publicationTime &&
      new Date(`${values.publicationDate}T${values.publicationTime}`) <=
        new Date()
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["publicationDate"],
        message: "Schedule a time in the future",
      });
    }
  });

export const paymentSchema = z
  .object({
    paymentSource: oneOf(PAYMENT_SOURCES),
    paymentMethodId: z.string(),
    paymentAuthorised: z
      .boolean()
      .refine((value) => value, "Authorise the payment to publish"),
  })
  .superRefine((values, ctx) => {
    if (values.paymentSource === "card" && !values.paymentMethodId) {
      ctx.addIssue({
        code: "custom",
        path: ["paymentMethodId"],
        message: "Select a card to pay with",
      });
    }
  });

// Index matches JOB_STEPS: STEP_SCHEMAS[step - 1] validates that step.
export const STEP_SCHEMAS = [
  requirementSourceSchema,
  roleDetailsSchema,
  jobContentSchema,
  skillsSchema,
  z.object({}), // Review job post has no inputs
  settingsSchema,
  publicationSchema,
  paymentSchema,
];

export const STEP_COUNT = STEP_SCHEMAS.length;

// Returns the first step (1-based) up to `lastStep` whose data is invalid, or
// null when all of them pass.
export const getFirstInvalidStep = (
  values: JobFormValues,
  lastStep = STEP_COUNT,
) => {
  for (let step = 1; step <= lastStep; step++) {
    if (!STEP_SCHEMAS[step - 1].safeParse(values).success) return step;
  }
  return null;
};
